<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <div v-if="isGuideSidebarStep" class="media-guide-screen-mask"></div>
    <div v-if="isGuideAddStep" class="media-guide-sidebar-blocker"></div>
    <VaAccordion :model-value="value" multiple @update:modelValue="updateExpandedSections">
      <VaCollapse
        v-for="(route, index) in menuObj.routes"
        :key="index"
        :class="{ 'media-guide-menu-target': isGuideTargetRoute(route) }"
      >
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="route.children ? undefined : { name: route.name }"
            :active="isGuideTargetRoute(route) || routeHasActiveChild(route)"
            :disabled="isGuideRouteDisabled(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
            role="button"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4">
              <VaIcon
                v-if="route.meta.icon"
                aria-hidden="true"
                :name="route.meta.icon"
                size="20px"
                :color="iconColor(route)"
              />
              <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                {{ t(route.displayName) }}
                <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
          <div v-if="isGuideTargetRoute(route)" class="media-guide-menu-hint">Click Publish</div>
        </template>
        <template #body>
          <div
            v-for="(childRoute, index2) in route.children"
            :key="index2"
            :class="{ 'media-guide-menu-target': isGuideTargetChild(childRoute) }"
          >
            <VaSidebarItem
              :to="{ name: childRoute.name }"
              :active="isGuideTargetChild(childRoute) || isActiveChildRoute(childRoute)"
              :disabled="isGuideChildRouteDisabled(childRoute)"
              :active-color="activeColor"
              :text-color="textColor(childRoute)"
              :aria-label="`Visit ${t(route.displayName)}`"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="py-3 pr-2 pl-11">
                <VaSidebarItemTitle class="leading-5 font-semibold">
                  {{ t(childRoute.displayName) }}
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
            <div v-if="isGuideTargetChild(childRoute)" class="media-guide-menu-hint">{{ guideMenuHint }}</div>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
    <div class="user-info">
      <div style="display:flex;align-items: center;">
         <ProfileDropdown class="app-navbar-actions__item app-navbar-actions__item--profile mr-1" />
          <div>
          <div class="info-item">
            <span class="nickname">{{ info.user.nickName }}</span>
          </div>
          <div class="info-item">
            <span class="account">{{ info.user.username }}</span>
          </div>
       </div>
      </div>
    </div>
  </VaSidebar>
</template>
<script lang="ts">
import ProfileDropdown from '../navbar/components/dropdowns/ProfileDropdown.vue'
import { defineComponent, watch, ref, computed, onMounted, onBeforeMount, reactive } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import { storeToRefs } from 'pinia'
import { useGlobalStore } from '../../stores/global-store'
//平台管理员
import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'
//店铺管理员
import Storemanager, { type INavigationRoute2 } from './Storemanager'
import Storeuser, { type INavigationRoute3 } from './Storeuser'
import { on } from 'stream'
import { getCurrentUser } from '@/api/index'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
    roleType: { type: String, default: '1' },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t } = useI18n()
    const globalStore = useGlobalStore()
    const { uploadMediaGuideStep, createSessionGuideStep, publishSessionGuideStep } = storeToRefs(globalStore)

    const isUploadMediaSidebarStep = computed(() => uploadMediaGuideStep.value === 'sidebar')
    const isUploadMediaAddStep = computed(() => uploadMediaGuideStep.value === 'add')
    const isCreateSessionSidebarStep = computed(() => createSessionGuideStep.value === 'sidebar')
    const isCreateSessionAddStep = computed(() => createSessionGuideStep.value === 'add')
    const isPublishSessionSidebarStep = computed(() => publishSessionGuideStep.value === 'sidebar')
    const isPublishSessionSelectStep = computed(() => publishSessionGuideStep.value === 'select')
    const isGuideSidebarStep = computed(
      () =>
        isUploadMediaSidebarStep.value ||
        isCreateSessionSidebarStep.value ||
        isPublishSessionSidebarStep.value,
    )
    const isGuideAddStep = computed(
      () =>
        isUploadMediaAddStep.value ||
        isCreateSessionAddStep.value ||
        isPublishSessionSelectStep.value,
    )
    const guideParentRouteName = computed(() =>
      isPublishSessionSidebarStep.value
        ? ''
        : isCreateSessionSidebarStep.value
          ? 'themeManagement'
          : 'assetManagement',
    )
    const guideChildRouteName = computed(() =>
      isPublishSessionSidebarStep.value ? '' : isCreateSessionSidebarStep.value ? 'themeList' : 'assetList',
    )
    const guideMenuHint = computed(() =>
      isCreateSessionSidebarStep.value ? 'Click Session' : 'Click Media',
    )

    const value = ref<boolean[]>([])

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return route.path.endsWith(`${section.name}`)
      }

      return section.children.some(({ name }) => route.path.endsWith(`${name}`))
    }

    const menuObj = reactive({
      root: {},
      routes: [],
    })
    const setGuideExpansion = () => {
      value.value = menuObj.routes.map((route: INavigationRoute) => route.name === guideParentRouteName.value)
    }
    const setActiveExpand = () => {
      if (isGuideSidebarStep.value) {
        setGuideExpansion()
        return
      }
      value.value = menuObj.routes.map((route: INavigationRoute) => routeHasActiveChild(route))
    }
    const updateExpandedSections = (nextValue: boolean[]) => {
      if (isGuideSidebarStep.value) {
        setGuideExpansion()
        return
      }
      value.value = nextValue
    }

    const isGuideTargetChild = (item: INavigationRoute) =>
      isGuideSidebarStep.value && item.name === guideChildRouteName.value
    const isGuideTargetRoute = (item: INavigationRoute) =>
      isPublishSessionSidebarStep.value && item.name === 'home'
    const isGuideRouteDisabled = (item: INavigationRoute) => {
      if (isGuideAddStep.value) return true
      if (!isGuideSidebarStep.value) return false
      if (isPublishSessionSidebarStep.value) return item.name !== 'home'
      return true
    }
    const isGuideChildRouteDisabled = (item: INavigationRoute) => {
      if (isGuideAddStep.value) return true
      if (!isGuideSidebarStep.value) return false
      return item.name !== guideChildRouteName.value
    }

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    let info = reactive({
      user: {
        nickName: '',
        username: ''
      }
    })
    const getUserInfo = () => {
      getCurrentUser().then((res) => {
        if(res.code == 200) {
          console.log('111111111111',res.data)
          let userinfo = res.data
          info.user.nickName = userinfo.user.nickName
          info.user.username = userinfo.user.username
        }
      })
      }
    onMounted(() => {
      //1平台管理员 2店铺管理员 3店铺用户
      if (props.roleType == '1') {
        menuObj.root = navigationRoutes.root
        menuObj.routes = navigationRoutes.routes
      } else if (props.roleType == '2') {
        menuObj.root = Storemanager.root
        menuObj.routes = Storemanager.routes
      } else if (props.roleType == '3') {
        menuObj.root = Storeuser.root
        menuObj.routes = Storeuser.routes
      } else {
        menuObj.root = navigationRoutes.root
        menuObj.routes = navigationRoutes.routes
      }
      getUserInfo()
    })

    watch(() => route.fullPath, () => {
      if (isUploadMediaSidebarStep.value && route.name === 'assetList') {
        globalStore.showUploadMediaAddStep()
      }
      if (isCreateSessionSidebarStep.value && route.name === 'themeList') {
        globalStore.showCreateSessionAddStep()
      }
      if (isPublishSessionSidebarStep.value && route.name === 'home') {
        globalStore.showPublishSessionSelectStep()
      }
      if (isUploadMediaAddStep.value && route.name !== 'assetList') {
        globalStore.finishUploadMediaGuide()
      }
      if (isCreateSessionAddStep.value && route.name !== 'themeList') {
        globalStore.finishCreateSessionGuide()
      }
      if (isPublishSessionSelectStep.value && route.name !== 'home') {
        globalStore.finishPublishSessionGuide()
      }
      setActiveExpand()
    }, { immediate: true })
    watch([uploadMediaGuideStep, createSessionGuideStep, publishSessionGuideStep], setActiveExpand)

    return {
      writableVisible,
      sidebarWidth,
      value,
      isGuideSidebarStep,
      isGuideAddStep,
      guideMenuHint,
      color,
      activeColor,
      navigationRoutes,
      Storemanager,
      Storeuser,
      menuObj,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      iconColor,
      textColor,
      arrowDirection,
      updateExpandedSections,
      isGuideTargetRoute,
      isGuideTargetChild,
      isGuideRouteDisabled,
      isGuideChildRouteDisabled,
      info
    }
  },
})
</script>
<style lang="scss"> 
.media-guide-screen-mask {
  position: fixed;
  inset: 0;
  z-index: 4000;
  background: rgba(0, 0, 0, 0.46);
}

.media-guide-menu-target {
  position: relative;
  z-index: 4001;
  background: var(--va-background-secondary);
  box-shadow: inset 0 0 0 3px var(--va-primary), 0 0 20px rgba(21, 78, 193, 0.45);
}

.media-guide-sidebar-blocker {
  position: absolute;
  inset: 0;
  z-index: 5002;
  background: rgba(0, 0, 0, 0.46);
  cursor: not-allowed;
}

.media-guide-menu-hint {
  position: absolute;
  top: 50%;
  right: 14px;
  z-index: 2;
  padding: 3px 8px;
  border-radius: 4px;
  color: #ffffff;
  background: var(--va-primary);
  font-size: 12px;
  font-weight: 700;
  pointer-events: none;
  transform: translateY(-50%);
}

.user-info {
    position: fixed;
    left: 20px;  /* 距离左边 20px */
    bottom: 20px;  /* 距离底部 20px */
    padding: 10px;  /* 适当的内边距 */
    border-radius: 5px;  /* 圆角 */
    font-size: 14px;  /* 文字大小 */
    z-index: 1000;  /* 确保在其他元素之上 */
  
}

/* 调整内部信息项 */
.info-item {
    margin-bottom: 5px;  /* 每一项之间的间距 */
}

.info-item {
  display: flex;
  justify-content: center; /* 标签和信息左右对齐 */
  align-items: flex-start; /* 上下对齐 */
}

.label {
  font-weight: bold;
  color: #333;
  font-size: 14px;
  text-align: right;
}

.nickname,
.account {
  color: #666;
  font-size: 14px;
  text-align: center; /* 信息部分左对齐 */
}

.nickname {
  font-weight: bold;
  font-size: 16px;
  color:#000000;
}

.account {
  font-weight: normal;
}

</style>
